import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, Sparkles, TrendingUp } from "lucide-react";
import { apiClient } from "../App";
import { UpgradeButton } from "./SubscriptionUI";

// -----------------------------------------------------------------------------
// PlanUsageContext: single source of truth for {plan, limits, usage}. Fetched
// from /api/subscription/status once and refreshed after any create/delete that
// affects a countable resource.
// -----------------------------------------------------------------------------

const PlanUsageContext = createContext(null);

export const PlanUsageProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, plan: "free", limits: {}, usage: {} });

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient.get("/subscription/status");
      setState({
        loading: false,
        plan: res.data?.plan || "free",
        limits: res.data?.limits || {},
        usage: res.data?.usage || {},
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <PlanUsageContext.Provider value={{ ...state, refresh }}>
      {children}
    </PlanUsageContext.Provider>
  );
};

export const usePlanUsage = () => {
  const ctx = useContext(PlanUsageContext);
  return ctx || { loading: true, plan: "free", limits: {}, usage: {}, refresh: () => {} };
};

// -----------------------------------------------------------------------------
// PlanUsageBadge: renders "N of X" pill for a given resource. Turns amber when
// >=80% and red when at limit. Silent for effectively-unlimited caps (>= 99).
// -----------------------------------------------------------------------------

const RESOURCE_META = {
  employee: { limitKey: "max_employees", usageKey: "employees", noun: "employee" },
  pay_group: { limitKey: "max_pay_groups", usageKey: "pay_groups", noun: "pay group" },
  invite: { limitKey: "max_pending_invites", usageKey: "pending_invites", noun: "pending invite" },
  location: { limitKey: "max_locations", usageKey: "locations", noun: "location" },
};

export const PlanUsageBadge = ({ resource, className = "" }) => {
  const { limits, usage, plan, loading } = usePlanUsage();
  const meta = RESOURCE_META[resource];
  if (!meta || loading) return null;

  const cap = limits[meta.limitKey];
  const count = usage[meta.usageKey] ?? 0;

  // Skip rendering for effectively unlimited tiers (Pro sets 99 or higher)
  if (cap === undefined || cap === null || cap >= 99) return null;

  const pct = cap > 0 ? count / cap : 0;
  const tone = pct >= 1 ? "bg-rose-100 text-rose-700" : pct >= 0.8 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";

  const nounPlural = cap === 1 ? meta.noun : `${meta.noun}s`;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone} ${className}`}
      data-testid={`plan-usage-${resource}`}
      title={`${plan[0].toUpperCase() + plan.slice(1)} plan: ${count} of ${cap} ${nounPlural} used`}
    >
      <TrendingUp className="w-3 h-3" />
      {count} of {cap} {nounPlural}
    </span>
  );
};

// -----------------------------------------------------------------------------
// PlanLimitDialog: shown when a create call returns HTTP 403 with a plan-limit
// message. Explains the current tier's cap, previews the next tier's cap, and
// offers an Upgrade CTA.
// -----------------------------------------------------------------------------

const NEXT_TIER = { free: "starter", starter: "pro", pro: null };

const nextTierCaps = (currentPlan, resource) => {
  const meta = RESOURCE_META[resource];
  if (!meta) return null;
  const next = NEXT_TIER[currentPlan];
  if (!next) return null;
  // Hard-coded next-tier caps mirror server.PLAN_LIMITS. Kept in one place so
  // the dialog copy stays honest without an extra API call.
  const table = {
    starter: { max_employees: 20, max_pay_groups: 3, max_pending_invites: 10, max_locations: 1 },
    pro: { max_employees: "unlimited", max_pay_groups: "unlimited", max_pending_invites: 50, max_locations: "unlimited" },
  };
  return { plan: next, cap: table[next]?.[meta.limitKey] };
};

export const PlanLimitDialog = ({ open, onOpenChange, resource, currentPlan, message }) => {
  const meta = RESOURCE_META[resource];
  const next = nextTierCaps(currentPlan, resource);
  const noun = meta?.noun || "resource";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="plan-limit-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <DialogTitle>You&apos;ve hit your {noun} limit</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {message ||
              `Your current plan doesn't allow adding another ${noun}.`}
          </DialogDescription>
        </DialogHeader>

        {next && next.cap !== undefined && (
          <div className="rounded-lg border border-slate-200 p-4 space-y-2 bg-slate-50/40">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#0A192F]">
              {next.plan === "pro" ? (
                <Crown className="w-4 h-4 text-amber-500" />
              ) : (
                <Sparkles className="w-4 h-4 text-blue-500" />
              )}
              Upgrade to {next.plan[0].toUpperCase() + next.plan.slice(1)}
            </div>
            <p className="text-sm text-slate-600">
              Get{" "}
              <span className="font-medium text-[#0A192F]">
                {next.cap === "unlimited" ? "unlimited" : `up to ${next.cap}`}{" "}
                {noun}s
              </span>
              {resource === "employee" && next.plan === "starter" && (
                <> — plus shift swap, overtime alerts, and auto clock-out.</>
              )}
              {resource === "employee" && next.plan === "pro" && (
                <> — plus multi-location, advanced reports, and audit log.</>
              )}
              {resource === "pay_group" && (
                <> — split your team by role, department, or pay cycle.</>
              )}
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="plan-limit-close-btn"
          >
            Not now
          </Button>
          {next && (
            <UpgradeButton plan={next.plan} compact size="default" />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Convenience hook: wraps an async action and pops the PlanLimitDialog when it
// throws a 403 that looks like a plan-limit error. Returns {run, dialogProps}.
// -----------------------------------------------------------------------------

export const usePlanLimitHandler = (resource) => {
  const { plan, refresh } = usePlanUsage();
  const [dialog, setDialog] = useState({ open: false, message: "" });

  const run = useCallback(
    async (action) => {
      try {
        const result = await action();
        // Successful action — refresh usage counts.
        refresh();
        return { ok: true, result };
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        // Treat both 403 (limit reached) and 402 (feature gated) with a
        // "plan limit"-shaped detail as the upgrade path.
        if ((status === 403 || status === 402) && /limit|upgrade|plan/i.test(detail)) {
          setDialog({ open: true, message: detail });
          return { ok: false, blocked: true, error: err };
        }
        return { ok: false, blocked: false, error: err };
      }
    },
    [refresh],
  );

  return {
    run,
    dialogProps: {
      open: dialog.open,
      onOpenChange: (v) => setDialog((d) => ({ ...d, open: v })),
      resource,
      currentPlan: plan,
      message: dialog.message,
    },
  };
};
